/**
 * Authentication state (CLAUDE.md 11.7).
 *
 * What this deliberately does **not** do: derive permissions from the token. The
 * access token carries `sub` and `system_role` only; workspace and project rights are
 * resolved server-side on every request, so a role change or a removal takes effect
 * immediately rather than at the next refresh. The client therefore asks the server
 * what it may do -- it never decides for itself.
 *
 * The active workspace is a request header validated against membership on every
 * call. Storing it here is a UI convenience and grants nothing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { ApiError, activeWorkspace, api, tokens } from '@/api/client'
import type { TokenResponse, User, WorkspaceSummary } from '@/api/types'
import { setLocale, type Locale } from '@/i18n'
import { useTheme, type ThemeChoice } from '@/theme/ThemeProvider'

interface AuthContextValue {
  user: User | null
  workspaces: WorkspaceSummary[]
  workspaceId: number | null
  /** Null until the first `/auth/me` settles, so routes can wait rather than flash. */
  loading: boolean
  login: (identifier: string, password: string) => Promise<void>
  register: (input: RegisterInput) => Promise<void>
  logout: () => Promise<void>
  switchWorkspace: (id: number) => void
  refreshUser: () => Promise<void>
  /**
   * Re-read the membership list.
   *
   * Needed because the list lives here rather than in TanStack Query -- the request
   * header and the workspace switcher both depend on it -- so creating an
   * organisation or accepting an invitation has no cache to invalidate. Without
   * this, a user who just created a workspace could not switch into it until they
   * reloaded the page.
   */
  refreshWorkspaces: () => Promise<WorkspaceSummary[]>
}

export interface RegisterInput {
  email: string
  username: string
  password: string
  display_name?: string
  locale?: string
  invitation_token?: string
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within <AuthProvider>')
  return context
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [workspaces, setWorkspaces] = useState<WorkspaceSummary[]>([])
  const [workspaceId, setWorkspaceId] = useState<number | null>(activeWorkspace.id)
  // Start "loading" only when there is actually a session to probe. Initialising to
  // `true` unconditionally forced the no-session case to setState synchronously
  // inside the mount effect, which is a cascading render for no reason.
  const [loading, setLoading] = useState(() => Boolean(tokens.access || tokens.refresh))
  const queryClient = useQueryClient()
  const { setTheme, choice } = useTheme()

  const applyUser = useCallback(
    (nextUser: User) => {
      setUser(nextUser)
      // The server is the source of truth for these once signed in, so the choice
      // follows the engineer to a new device (14.2, 14.4).
      if (nextUser.locale && nextUser.locale !== document.documentElement.lang) {
        setLocale(nextUser.locale as Locale)
      }
      if (nextUser.theme_preference && nextUser.theme_preference !== choice) {
        setTheme(nextUser.theme_preference as ThemeChoice)
      }
    },
    [choice, setTheme],
  )

  const loadSession = useCallback(async () => {
    if (!tokens.access && !tokens.refresh) {
      // Nothing to probe: `loading` already started false.
      return
    }
    try {
      // Sequential, not `Promise.all`. `/auth/me` is deliberately reachable while a
      // password change is owed; every other endpoint answers 403 until it is done.
      // Fetching both together made that 403 reject the pair, so the session probe
      // fell into the catch below and signed the user out -- the first-run
      // administrator could log in successfully and land straight back on /login,
      // forever. The user *is* authenticated; they are just fenced in.
      const me = await api.get<User>('/auth/me')
      applyUser(me)

      if (me.password_change_required) {
        // `ProtectedRoute` sends them to /settings/password from here.
        setWorkspaces([])
        return
      }

      const list = await api.get<WorkspaceSummary[]>('/auth/me/workspaces')
      setWorkspaces(list)

      // Fall back to the personal workspace: a solo user never has to know
      // workspaces exist (ADR-0002).
      const stored = activeWorkspace.id
      const valid = stored !== null && list.some((w) => w.id === stored)
      const next = valid ? stored : (me.personal_workspace_id ?? list[0]?.id ?? null)
      setWorkspaceId(next)
      activeWorkspace.set(next)
    } catch (error) {
      // An expired or revoked session is not an error worth showing -- it is a
      // sign-in prompt.
      if (error instanceof ApiError && error.status === 401) {
        tokens.clear()
      }
      setUser(null)
    } finally {
      setLoading(false)
    }
  }, [applyUser])

  useEffect(() => {
    // `loadSession` only ever sets state *after* an await, so nothing here is a
    // synchronous cascading render -- the rule cannot see across the async boundary.
    // The empty dep list is intentional: re-running on every `applyUser` identity
    // change would refetch the session on each theme toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps, react-hooks/set-state-in-effect
    void loadSession()
  }, [])

  const login = useCallback(
    async (identifier: string, password: string) => {
      const response = await api.publicPost<TokenResponse>('/auth/login', {
        identifier,
        password,
      })
      tokens.set(response.access_token, response.refresh_token)
      setLoading(true)
      await loadSession()
    },
    [loadSession],
  )

  const register = useCallback(
    async (input: RegisterInput) => {
      await api.publicPost<User>('/auth/register', input)
      await login(input.email, input.password)
    },
    [login],
  )

  const logout = useCallback(async () => {
    try {
      // Revokes the refresh-token family server-side. A failure here still signs the
      // user out locally: a stuck session is worse than an orphaned server row.
      await api.post('/auth/logout', { refresh_token: tokens.refresh })
    } catch {
      // Deliberately ignored, see above.
    }
    tokens.clear()
    activeWorkspace.set(null)
    setUser(null)
    setWorkspaces([])
    setWorkspaceId(null)
    // Every cached query was scoped to the user who just left.
    queryClient.clear()
  }, [queryClient])

  const switchWorkspace = useCallback(
    (id: number) => {
      setWorkspaceId(id)
      activeWorkspace.set(id)
      // All server state is workspace-scoped; keeping it would show the previous
      // workspace's projects under the new one's name.
      queryClient.clear()
    },
    [queryClient],
  )

  const refreshUser = useCallback(async () => {
    const me = await api.get<User>('/auth/me')
    applyUser(me)
  }, [applyUser])

  const refreshWorkspaces = useCallback(async () => {
    const list = await api.get<WorkspaceSummary[]>('/auth/me/workspaces')
    setWorkspaces(list)
    // Returned as well as stored: a caller that has just created or joined a
    // workspace wants to switch into it in the same turn, and reading `workspaces`
    // back from context in that turn would still see the previous render's value.
    return list
  }, [])

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      workspaces,
      workspaceId,
      loading,
      login,
      register,
      logout,
      switchWorkspace,
      refreshUser,
      refreshWorkspaces,
    }),
    [
      user,
      workspaces,
      workspaceId,
      loading,
      login,
      register,
      logout,
      switchWorkspace,
      refreshUser,
      refreshWorkspaces,
    ],
  )

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}
